import { IsoCubeFaces } from "../../schemas/schema.js";
import { IsoProjectionParams, usesLayeredCellDraw } from "./projection.js";

export type VisibleCubeFaces = {
    top: IsoCubeFaces["top"];
    left: IsoCubeFaces["north"] | IsoCubeFaces["east"] | IsoCubeFaces["south"] | IsoCubeFaces["west"];
    right: IsoCubeFaces["north"] | IsoCubeFaces["east"] | IsoCubeFaces["south"] | IsoCubeFaces["west"];
};

/**
 * Maps intrinsic cube face colours to the three faces visible in the fixed isometric projection.
 *
 * At yaw 0 the left parallelogram shows the south face (front toward lower ranks) and the right
 * parallelogram shows the east face. Additional yaws are quarter-turns clockwise viewed from above
 * (so yaw 1 puts east on the left and north on the right).
 *
 * @param faces - Intrinsic face colours (N/E/S/W + top).
 * @param effectiveYaw - Quarter-turns clockwise viewed from above, including board rotation.
 */
export const permuteCubeFaces = (faces: IsoCubeFaces, effectiveYaw: number): VisibleCubeFaces => {
    const yaw = ((effectiveYaw % 4) + 4) % 4;
    switch (yaw) {
        case 1:
            return { top: faces.top, left: faces.east, right: faces.north };
        case 2:
            return { top: faces.top, left: faces.north, right: faces.west };
        case 3:
            return { top: faces.top, left: faces.west, right: faces.south };
        default:
            return { top: faces.top, left: faces.south, right: faces.east };
    }
};

/** Maps face colours for the active projection (cabinet swaps east/west before iso permute). */
export const permuteCubeFacesForProjection = (
    faces: IsoCubeFaces,
    effectiveYaw: number,
    params: IsoProjectionParams,
): VisibleCubeFaces => {
    if (!usesLayeredCellDraw(params)) {
        return permuteCubeFaces(faces, effectiveYaw);
    }
    return permuteCubeFaces(
        { ...faces, east: faces.west, west: faces.east },
        effectiveYaw,
    );
};

/**
 * Combines piece yaw with board rotation (in degrees) into a single effective yaw (0–3).
 */
export const effectiveCubeYaw = (pieceYaw: number, boardRotationDeg: number): number => {
    const boardQuarterTurns = Math.floor(boardRotationDeg / 90) % 4;
    return ((pieceYaw + boardQuarterTurns) % 4 + 4) % 4;
};

/**
 * Isometric board rotation only supports quarter turns. Non-90° remainders are dropped
 * (e.g. 45° → 0°, 135° → 90°).
 */
export const snapRotationToQuarterTurns = (boardRotationDeg: number): number => {
    let rotation = boardRotationDeg % 360;
    while (rotation < 0) {
        rotation += 360;
    }
    return 90 * Math.floor(rotation / 90);
};
